import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Autocomplete,
  Radio,
  RadioGroup,
  TextareaAutosize,
  TextField,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import ServerMessageSnackbar from "../../../../reusableComponents/serverMessageSnackbar/serverMessageSnackbar";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  getClients,
  getUsers,
  sendEmailNotification,
} from "../../../../../store/thunks/systemManagementThunk";
import Spinner from "../../../../reusableComponents/spinner/spinner";
import { all } from "axios";

const EmailNotification = () => {
  const dispatch = useAppDispatch();
  const {
    allClients,
    allUsers,
    getClientsLoading,
    getUserLoading,
    sendEmailNotificationLoading,
  } = useAppSelector((state: any) => state.systemManagement);

  const [emailNotificationError, setEmailNotificationError] = useState(false);
  const [emailNotificationErrorMessage, setEmailNotificationErrorMessage] =
    useState("");

  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [clientError, setClientError] = useState(false);
  const [clientErrorMessage, setClientErrorMessage] = useState("");
  const [userError, setUserError] = useState(false);
  const [userErrorMessage, setUserErrorMessage] = useState("");

  const validateEmailNotification = Yup.object({
    subject: Yup.string().required("Subject is Required"),
    message: Yup.string().required("Message is Required"),
    notification_type: Yup.string().required("Notification Type is Required"),
    clients: Yup.mixed(),
    users: Yup.mixed(),
  });

  const emailNotifiicationForm = useFormik({
    initialValues: {
      subject: "",
      message: "",
      notification_type: "",
      clients: [],
      users: [],
    },
    validationSchema: validateEmailNotification,
    enableReinitialize: true,
    onSubmit: (values: any) => {
      if (values.notification_type === "specific_users") {
        if (values.users && values.users?.length === 0) {
          setUserError(true);
          setUserErrorMessage("Users are required");
          return;
        }
      }
      if (values.notification_type === "specific_clients") {
        if (values.clients && values.clients?.length === 0) {
          setClientError(true);
          setClientErrorMessage("Clients are required");
          return;
        }
      }
      dispatch(
        sendEmailNotification({
          sendEmailNotificationPayload: values,
        })
      )
        .then((emailNotificationResult: any) => {
          setEmailNotificationError(false);
          setEmailNotificationErrorMessage("");
          if (emailNotificationResult.payload.result) {
            setSnackbarOpen(true);
            setSnackbarMessage(emailNotificationResult.payload.message);
            emailNotifiicationForm.resetForm();
          } else {
            setEmailNotificationError(true);
            setEmailNotificationErrorMessage(
              emailNotificationResult.payload.message
            );
          }
        })
        .catch((emailNotificationError: any) => {
          setEmailNotificationError(true);
          setEmailNotificationErrorMessage(emailNotificationError.message);
        });
    },
  });

  const handleNotificationTypeChange = (e: any) => {
    setClientError(false);
    setClientErrorMessage("");
    setUserError(false);
    setUserErrorMessage("");
    emailNotifiicationForm.setFieldValue("notification_type", e.target.value);
    emailNotifiicationForm.setFieldValue("clients", []);
    emailNotifiicationForm.setFieldValue("users", []);
  };

  useEffect(() => {
    if (emailNotifiicationForm.values.notification_type !== "") {
      emailNotifiicationForm.setFieldError("notification_type", "");
    }
  }, [emailNotifiicationForm.values.notification_type]);

  useEffect(() => {
    if (allUsers && allUsers.length > 0) {
      const tempUserOptions: any[] = [];
      allUsers.forEach((user: any) => {
        if (user.status === "Active") {
          tempUserOptions.push(user);
        }
      });
      setUserOptions(tempUserOptions);
    } else {
      dispatch(getUsers({}));
    }

    if (allClients) {
      const tempClientOptions: any[] = [];
      allClients.forEach((client: any) => {
        if (client.client_status === "Active") {
          tempClientOptions.push(client);
        }
      });
      setClientOptions(tempClientOptions);
    }
  }, [allClients, allUsers]);

  return (
    <Grid container>
      <Grid
        container
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px 10px 20px",
        }}
      >
        <Typography variant="h4" sx={{ color: "#004680", fontWeight: "bold" }}>
          {"Email Notification"}
        </Typography>
      </Grid>
      {/* <Spinner loading={getUserLoading} /> */}
      <Spinner loading={sendEmailNotificationLoading} />
      <form
        onSubmit={emailNotifiicationForm.handleSubmit}
        style={{ width: "100%" }}
      >
        <Grid
          container
          item
          xs={12}
          sx={{ padding: "10px 20px 10px 20px", width: "75%" }}
        >
          <TextField
            fullWidth
            placeholder="Subject"
            label="Subject"
            id="subject"
            name="subject"
            value={emailNotifiicationForm.values.subject}
            onChange={emailNotifiicationForm.handleChange}
            error={
              emailNotifiicationForm.touched.subject &&
              Boolean(emailNotifiicationForm.errors.subject)
            }
            helperText={emailNotifiicationForm.errors.subject as string}
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          sx={{ padding: "10px 20px 10px 20px", width: "75%" }}
        >
          <TextareaAutosize
            style={{
              width: "100%",
              border: "1px solid #000",
              borderRadius: "5px",
              padding: "10px",
            }}
            placeholder="Message"
            id="message"
            name="message"
            value={emailNotifiicationForm.values.message}
            onChange={emailNotifiicationForm.handleChange}
            minRows={10}
            maxRows={10}
          />
          <Grid container item xs={12} sx={{}}>
            {emailNotifiicationForm.touched.message &&
              emailNotifiicationForm.errors.message && (
                <Typography variant="body1" color="error">
                  {emailNotifiicationForm.errors.message as string}
                </Typography>
              )}
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          sx={{ padding: "10px 20px 10px 20px", width: "75%" }}
        >
          <Grid container item xs={12} sx={{}}>
            <FormControl>
              <FormLabel id="demo-row-radio-buttons-group-label">
                Notification Recipients
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
              >
                <FormControlLabel
                  value="all_clients"
                  control={
                    <Radio
                      checked={
                        emailNotifiicationForm.values.notification_type ===
                        "all_clients"
                      }
                      onChange={(e) => handleNotificationTypeChange(e)}
                    />
                  }
                  label="All Clients"
                />
                <FormControlLabel
                  value="specific_clients"
                  control={
                    <Radio
                      checked={
                        emailNotifiicationForm.values.notification_type ===
                        "specific_clients"
                      }
                      onChange={(e) => handleNotificationTypeChange(e)}
                    />
                  }
                  label="Specific Clients"
                />
                <FormControlLabel
                  value="specific_users"
                  control={
                    <Radio
                      checked={
                        emailNotifiicationForm.values.notification_type ===
                        "specific_users"
                      }
                      onChange={(e) => handleNotificationTypeChange(e)}
                    />
                  }
                  label="Specific Users"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid container item xs={12} sx={{}}>
            {emailNotifiicationForm.touched.notification_type &&
              emailNotifiicationForm.errors.notification_type && (
                <Typography variant="body1" color="error">
                  {emailNotifiicationForm.errors.notification_type as string}
                </Typography>
              )}
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          sx={{ padding: "10px 20px 10px 20px", width: "100%" }}
        >
          {emailNotifiicationForm.values.notification_type ===
            "specific_clients" && (
            <Grid container item xs={12} sx={{}}>
              <Grid container item xs={12} sx={{}}>
                <Typography variant="h6">
                  Notification will be sent to all selected active users for the
                  selected clients
                </Typography>
              </Grid>
              <Grid
                container
                item
                xs={12}
                sx={{ padding: "10px 0px 10px 0px", width: "100%" }}
              >
                <Autocomplete
                  options={clientOptions ? clientOptions : []}
                  getOptionLabel={(option: any) => option?.customer_name}
                  isOptionEqualToValue={(option, value) =>
                    option?.customer_id === value?.customer_id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Clients"
                      error={clientError}
                      helperText={clientErrorMessage}
                    />
                  )}
                  multiple
                  onChange={(e, value) => {
                    setClientError(false);
                    setClientErrorMessage("");
                    if (value && value?.length > 0) {
                      emailNotifiicationForm.setFieldValue("clients", value);
                    } else {
                      emailNotifiicationForm.setFieldValue("clients", []);
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </Grid>
            </Grid>
          )}
          {emailNotifiicationForm.values.notification_type ===
            "specific_users" && (
            <Grid container item xs={12} sx={{}}>
              <Grid container item xs={12} sx={{}}>
                <Typography variant="h6">
                  Notification will be sent to all selected active users
                </Typography>
              </Grid>
              <Grid
                container
                item
                xs={12}
                sx={{ padding: "10px 0px 10px 0px", width: "100%" }}
              >
                <Autocomplete
                  options={userOptions ? userOptions : []}
                  getOptionLabel={(option: any) =>
                    option?.first_name +
                    " " +
                    option?.last_name +
                    " - " +
                    option?.user_name +
                    " - " +
                    option?.status
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.client_user_index === value?.client_user_index
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Users"
                      error={userError}
                      helperText={userErrorMessage}
                    />
                  )}
                  multiple
                  onChange={(e, value) => {
                    setUserError(false);
                    setUserErrorMessage("");
                    if (value && value?.length > 0) {
                      emailNotifiicationForm.setFieldValue("users", value);
                    } else {
                      emailNotifiicationForm.setFieldValue("users", []);
                    }
                  }}
                  style={{ width: "75%" }}
                />
              </Grid>
            </Grid>
          )}
          {emailNotifiicationForm.values.notification_type ===
            "all_clients" && (
            <Grid container item xs={12} sx={{}}>
              <Typography variant="h6">
                Notificattion will be broadcasted to all active users for all
                clients
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid container item xs={12} sx={{ padding: "10px 20px 10px 20px" }}>
          <Button
            type="submit"
            variant="outlined"
            sx={{
              padding: "5px 10px 5px 10px",
              backgroundColor: "rgba(59, 147, 74, 1)",
              color: "white",
            }}
          >
            Send Notification
          </Button>
        </Grid>
      </form>
      <ServerMessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
      />
    </Grid>
  );
};

export default EmailNotification;
